import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsInput,
  portalDs,
} from 'src/components/portal-design-system';
import ManageClientAccountCampaignSelection from './manage-client-account-campaign-selection';
import { useGetAccountsQuery, useGetCampaignsQuery } from '../../endpoints/accounts.endpoints';
import { useGetCompaniesQuery } from 'src/redux/billing/billing.endpoints';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { useLiveActiveClient } from '../../hooks/use-live-active-client';
import { useUpdateClientAccountAccessMutation, useUpdateClientMutation } from '../../endpoints/client.endpoints';
import { ClientAccess, ClientView, ReplaceClientAccountAccessRequest } from '../../types/client.types';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';

interface ManageClientAccessProps {
  onDirtyChange?: (dirty: boolean) => void;
  onSelectionChange?: (accountIds: string[], campaignIds: string[]) => void;
  onOpenChange?: (open: boolean) => void;
  headerActionsEl?: HTMLElement | null;
}

interface SelectedCampaign {
  source: string;
  accountId: string; // ext_account_id
  campaignId: string; // ext_campaign_id
}

const ManageClientAccess: React.FC<ManageClientAccessProps> = ({
  onDirtyChange,
  onSelectionChange,
  headerActionsEl,
}) => {
  const { setClient } = useActiveClient();
  const liveClient = useLiveActiveClient();
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<SelectedCampaign[]>([]);
  const [accountFilter] = useState<string[]>([]);
  const [companyFilter] = useState<string[]>([]);
  const [savedFlash, setSavedFlash] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectionClientId, setSelectionClientId] = useState<string | null>(null);
  const [committedSelectionKey, setCommittedSelectionKey] = useState<string | null>(null);

  const { data: accountsPage, isLoading: isAccountsLoading } = useGetAccountsQuery();
  const { data: campaignsPage, isLoading: isCampaignsLoading } = useGetCampaignsQuery();
  const accounts = accountsPage?.items;
  const campaigns = campaignsPage?.items;
  const { data: companies } = useGetCompaniesQuery();
  const [updateClientAccountAccess] = useUpdateClientAccountAccessMutation();
  const [updateClient] = useUpdateClientMutation();

  // Extract existing access from activeClient.access and convert to SelectedCampaign format
  const existingCampaigns = useMemo<SelectedCampaign[]>(() => {
    if (!liveClient?.access || !accounts || !campaigns) return [];

    return liveClient.access
      .map(access => {
        // Find account by ext_account_id or id
        const account = accounts.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
        // Find campaign by ext_campaign_id or id
        const campaign = campaigns.find(camp => camp.ext_campaign_id === access.campaign || camp.id === access.campaign);

        if (account?.ext_account_id && campaign?.ext_campaign_id) {
          return {
            source: account.source, // Get source from account
            accountId: account.ext_account_id,
            campaignId: campaign.ext_campaign_id,
          };
        }
        return null;
      })
      .filter((item): item is SelectedCampaign => item !== null);
  }, [liveClient?.access, accounts, campaigns]);

  // Extract unique account IDs from existing access
  const existingAccountIds = useMemo(() => {
    if (!liveClient?.access || !accounts) return [];
    const accountIds = liveClient.access
      .map(access => {
        const account = accounts.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
        return account?.ext_account_id;
      })
      .filter(Boolean) as string[];
    return Array.from(new Set(accountIds));
  }, [liveClient?.access, accounts]);

  useEffect(() => {
    setSelectionClientId(null);
    setCommittedSelectionKey(null);
  }, [liveClient?.ext_client_id]);

  useEffect(() => {
    if (!liveClient?.ext_client_id || !accounts || !campaigns) {
      return;
    }
    if (selectionClientId === liveClient.ext_client_id) {
      return;
    }

    if (liveClient.access?.length) {
      setSelectedAccountIds(existingAccountIds);
      setSelectedCampaigns(existingCampaigns);
    } else {
      setSelectedAccountIds([]);
      setSelectedCampaigns([]);
    }
    setSelectionClientId(liveClient.ext_client_id);
  }, [
    liveClient?.ext_client_id,
    liveClient?.access,
    accounts,
    campaigns,
    existingAccountIds,
    existingCampaigns,
    selectionClientId,
  ]);

  // Convert selectedCampaigns to campaignIds array for the selection component
  const selectedCampaignIds = useMemo(() => {
    return selectedCampaigns.map(sc => sc.campaignId);
  }, [selectedCampaigns]);

  // Calculate what needs to be added and removed
  const accessChanges = useMemo(() => {
    const toAdd: SelectedCampaign[] = [];
    const toRemove: ClientAccess[] = [];

    // Create a map of existing access for quick lookup
    const existingMap = new Map<string, ClientAccess>();
    liveClient?.access?.forEach(access => {
      const key = `${access.account}:${access.campaign}`;
      existingMap.set(key, access);
    });

    // Create a map of selected campaigns for quick lookup
    const selectedMap = new Map<string, SelectedCampaign>();
    selectedCampaigns.forEach(sc => {
      const key = `${sc.accountId}:${sc.campaignId}`;
      selectedMap.set(key, sc);
    });

    // Find campaigns to add (in selected but not in existing)
    selectedCampaigns.forEach(sc => {
      // Find matching existing access by account and campaign
      const existingAccess = liveClient?.access?.find(access => {
        const account = accounts?.find(acc => acc.ext_account_id === sc.accountId || acc.id === sc.accountId);
        const campaign = campaigns?.find(camp => camp.ext_campaign_id === sc.campaignId || camp.id === sc.campaignId);
        return (
          (account && (account.ext_account_id === access.account || account.id === access.account)) &&
          (campaign && (campaign.ext_campaign_id === access.campaign || campaign.id === access.campaign))
        );
      });

      if (!existingAccess) {
        toAdd.push(sc);
      }
    });

    // Find campaigns to remove (in existing but not in selected)
    liveClient?.access?.forEach(access => {
      const account = accounts?.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
      const campaign = campaigns?.find(camp => camp.ext_campaign_id === access.campaign || camp.id === access.campaign);

      if (account?.ext_account_id && campaign?.ext_campaign_id) {
        const key = `${account.ext_account_id}:${campaign.ext_campaign_id}`;
        if (!selectedMap.has(key)) {
          toRemove.push(access);
        }
      }
    });

    return { toAdd, toRemove };
  }, [selectedCampaigns, liveClient?.access, accounts, campaigns]);

  const createDefaultClientView = (accountId: string, accountName: string, platform: string): ClientView => ({
    account_ext_id: accountId,
    account_name: accountName,
    platform: platform,
    show_difference: false,
    metrics: [],
    graph_one: {
      metric_one: null,
      metric_two: null,
    },
    graph_two: {
      metric_one: null,
      metric_two: null,
    },
    graph_three: {
      metric_one: null,
      metric_two: null,
    },
  });

  const handleSave = async() => {
    if (!liveClient?.ext_client_id) return;

    setIsSaving(true);
    try {
      let updatedClient = {
        ...liveClient,
        views: [...liveClient.views],
      };

      for (const sc of accessChanges.toAdd) {
        const hasView = updatedClient.views.some((view) => view.account_ext_id === sc.accountId);
        if (!hasView) {
          const accountName = accounts?.find((acc) => acc.ext_account_id === sc.accountId)?.title;
          const newView = createDefaultClientView(sc.accountId, accountName ?? '', sc.source);
          updatedClient = {
            ...updatedClient,
            views: [...updatedClient.views, newView],
          };
          await updateClient({
            clientId: liveClient.ext_client_id,
            data: updatedClient,
          }).unwrap();
          setClient(updatedClient);
        }
      }

      const campaignsByAccountSource = new Map<string, string[]>();
      selectedCampaigns.forEach((sc) => {
        const key = `${sc.accountId}:${sc.source}`;
        if (!campaignsByAccountSource.has(key)) {
          campaignsByAccountSource.set(key, []);
        }
        campaignsByAccountSource.get(key)!.push(sc.campaignId);
      });

      const accessPayload: ReplaceClientAccountAccessRequest = {
        ext_client_id: liveClient.ext_client_id,
        access: Array.from(campaignsByAccountSource.entries()).map(([key, campaignIds]) => {
          const [accountId, source] = key.split(':');
          return {
            source,
            account: accountId,
            campaigns: campaignIds,
          };
        }),
      };

      await updateClientAccountAccess(accessPayload).unwrap();
      setCommittedSelectionKey(
        [...selectedCampaigns]
          .map((item) => `${item.accountId}:${item.campaignId}`)
          .sort()
          .join('|')
      );

      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1600);
      notificationService.handleSuccess(null, dispatch, 'Client access saved successfully');
    } catch (error) {
      console.error('Error saving access:', error);
      notificationService.handleError(error, dispatch, 'Error saving client access');
    } finally {
      setIsSaving(false);
    }
  };

  const visibleCampaigns = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return (campaigns ?? []).filter((campaign) => {
      if (!query) {
        return true;
      }
      return campaign.title.toLowerCase().includes(query);
    });
  }, [campaigns, searchValue]);

  const companyChips = useMemo(() => {
    const companyIds = new Set<string>();
    selectedCampaigns.forEach((selected) => {
      const campaign = campaigns?.find((item) => item.ext_campaign_id === selected.campaignId);
      const companyId = campaign?.company ?? campaign?.account?.company;
      if (companyId) {
        companyIds.add(companyId);
      }
    });
    return (companies ?? []).filter((company) => companyIds.has(company.id));
  }, [selectedCampaigns, campaigns, companies]);

  const handleSelectionChange = (accountIds: string[], campaignIds: string[]) => {
    setSelectedAccountIds(accountIds);

    const newSelectedCampaigns: SelectedCampaign[] = [];
    campaignIds.forEach((campaignId) => {
      const campaign = campaigns?.find((item) => item.ext_campaign_id === campaignId);
      if (campaign?.account?.ext_account_id) {
        newSelectedCampaigns.push({
          source: campaign.account.source,
          accountId: campaign.account.ext_account_id,
          campaignId: campaign.ext_campaign_id,
        });
      }
    });

    setSelectedCampaigns(newSelectedCampaigns);
    onSelectionChange?.(accountIds, campaignIds);
  };

  const handleSelectAllVisible = () => {
    const campaignIds = visibleCampaigns.map((campaign) => campaign.ext_campaign_id);
    const accountIds = [...new Set(
      visibleCampaigns
        .map((campaign) => campaign.account?.ext_account_id)
        .filter((id): id is string => Boolean(id))
    )];
    handleSelectionChange(accountIds, campaignIds);
  };

  const handleClearAll = () => {
    handleSelectionChange([], []);
  };

  const selectedCount = selectedCampaigns.length;
  const liveAccountCount = useMemo(
    () => new Set(selectedCampaigns.map((item) => item.accountId)).size,
    [selectedCampaigns]
  );
  const selectionKey = useMemo(
    () => [...selectedCampaigns]
      .map((item) => `${item.accountId}:${item.campaignId}`)
      .sort()
      .join('|'),
    [selectedCampaigns]
  );
  const summaryLabel = selectedCount === 0
    ? 'No campaigns selected yet.'
    : `${selectedCount} campaign${selectedCount === 1 ? '' : 's'} selected${
      companyChips.length > 0
        ? ` across ${companyChips.length} compan${companyChips.length === 1 ? 'y' : 'ies'}.`
        : '.'
    }`;

  const derivedDirty = accessChanges.toAdd.length > 0 || accessChanges.toRemove.length > 0;
  const hasChanges = derivedDirty && committedSelectionKey !== selectionKey;

  useEffect(() => {
    onDirtyChange?.(hasChanges);
    return () => onDirtyChange?.(false);
  }, [hasChanges, onDirtyChange]);

  const isListLoading = isAccountsLoading || isCampaignsLoading;

  const saveButton = (
    <PortalDsButton
      variant="primary"
      size="sm"
      disabled={!hasChanges || isSaving}
      onClick={() => void handleSave()}
    >
      {isSaving ? 'Saving…' : 'Save changes'}
    </PortalDsButton>
  );

  return (
    <div className="flex flex-col gap-4">
      {headerActionsEl ? createPortal(saveButton, headerActionsEl) : (
        <div className="flex justify-end">
          {saveButton}
        </div>
      )}

      {savedFlash ? (
        <PortalDsAlert variant="success">
          Access changes saved.
        </PortalDsAlert>
      ) : null}

      <div
        className="flex gap-5 rounded-[10px] border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3"
        aria-live="polite"
      >
        <div>
          <span className={portalDs.sectionLabel}>
            Users
          </span>
          <span className="ml-1.5 text-[13.5px] font-bold text-[#0D0F1A]">
            {liveClient?.users?.length ?? 0}
          </span>
        </div>
        <div>
          <span className={portalDs.sectionLabel}>
            Accounts
          </span>
          <span
            className="ml-1.5 text-[13.5px] font-bold text-[#0D0F1A]"
            data-testid="access-account-count"
          >
            {liveAccountCount}
          </span>
        </div>
        <div>
          <span className={portalDs.sectionLabel}>
            Campaigns
          </span>
          <span
            className="ml-1.5 text-[13.5px] font-bold text-[#0D0F1A]"
            data-testid="access-campaign-count"
          >
            {selectedCount}
          </span>
        </div>
      </div>

      <p className={portalDs.bodySm}>
        Search for a campaign to grant this client access to it, or select whole accounts and companies at once.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-[300px] max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#9CA3AF]" />
          <PortalDsInput
            value={searchValue}
            placeholder="Search campaigns..."
            className="h-10 pl-9"
            disabled={isListLoading}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
        <div className="ml-auto flex gap-2">
          <PortalDsButton
            variant="secondary"
            size="sm"
            disabled={isListLoading}
            onClick={handleSelectAllVisible}
          >
            Select all visible
          </PortalDsButton>
          <PortalDsButton
            variant="ghost"
            size="sm"
            disabled={isListLoading}
            onClick={handleClearAll}
          >
            Clear all
          </PortalDsButton>
        </div>
      </div>

      {companyChips.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {companyChips.map((company) => (
            <span
              key={company.id}
              className="inline-flex items-center rounded-full border-[1.5px] border-[#0D0F1A] bg-[#0D0F1A] px-3 py-1 text-xs font-semibold text-white"
            >
              {company.company_name}
            </span>
          ))}
        </div>
      ) : null}

      <ManageClientAccountCampaignSelection
        accounts={accounts || []}
        campaigns={campaigns || []}
        companies={companies || []}
        selectedCompanies={[]}
        selectedAccountIds={selectedAccountIds}
        selectedCampaignIds={selectedCampaignIds}
        showCompanyGrouping={true}
        isLoading={isListLoading}
        searchValue={searchValue}
        accountFilter={accountFilter}
        companyFilter={companyFilter}
        onSelectionChange={handleSelectionChange}
      />

      <p className={portalDs.bodySm}>
        {summaryLabel}
      </p>
    </div>
  );
};

export default ManageClientAccess;
